import EntityMember from "_shared/entity-members/entity-member";
import { withoutFeeScheduleTeamMember } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import FeeScheduleCategoryCommand, {
  type FeeScheduleCategoryCommandPayload,
} from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-category-command";
import { Masked, type MaskedContracts } from "work/masked-fields";
import { ProposalTemplateParty } from "work/proposal-templates/proposal-template";
import type Proposal from "work/proposals/proposal";
import type WorkDocument from "work/work-agreements/contracts/work-document";

type PersonPayload = { userId: string; entityId: string };
type TeamPayload = {
  leader?: PersonPayload;
  members: PersonPayload[];
  marketplaceId?: string;
};

/**
 * Which teams the saver keeps in the template (#6637), as the Save as Template dialog asks.
 *
 * Their own side's team by default, and not the other party's: those people are not the saver's to
 * manage. Kept when asked, the other party's team is stored as references to its people, so their
 * profiles and visibility still decide what a draft started from the template shows.
 */
export type ProposalTemplateTeamChoices = {
  /** The saver's own side's team: the client team for a client, the vendor team for a firm. */
  ownTeam: boolean;
  /** The other party's team. */
  otherTeam: boolean;
  /** The team templates applied to the other party's team. Only with `otherTeam`. */
  otherTeamTemplates: boolean;
};

export const DEFAULT_TEAM_CHOICES: ProposalTemplateTeamChoices = {
  ownTeam: true,
  otherTeam: false,
  otherTeamTemplates: false,
};

export type SaveProposalTemplatePayload = {
  name: string;
  party: ProposalTemplateParty;
  proposalName: string;
  description: string;
  negotiable: boolean;
  teamRestricted: boolean;
  conflictsCheckWaived: boolean;
  team?: TeamPayload;
  feeSchedule: FeeScheduleCategoryCommandPayload[];
  discount: number;
  teamTemplateIds: string[];
  /**
   * The proposal the template is saved from. The server keeps the other party's team and its
   * templates only as this proposal shows them to the saver (review of #6798).
   */
  sourceProposalId?: string;
  /** The other party's team, when the saver chose to keep it (#6637). */
  counterpartyTeam?: TeamPayload;
  /** The team templates applied to the other party's team, when the saver kept them too. */
  counterpartyTeamTemplateIds: string[];
  feeScheduleTemplateIds: string[];
  /** The proposal's conflicts documents. The server copies each into the template. */
  conflicts: string[];
  /** The saver's side's policies. The server copies each into the template. */
  policies: string[];
};

/**
 * `POST work/proposal-templates`: one side of a proposal, saved as a named template (#6129).
 *
 * Built from `carried`, the proposal as a new draft would carry it over, so what it holds has been
 * through the same rules as Create from Previous: masked fields are empty and people who have left
 * are gone. From that it keeps the saver's side: their team, rates, policies and template lists.
 *
 * The other party's team stays behind unless the saver asks to keep it, with or without the team
 * templates applied to it (#6637); it is then sent as references to its people, never copies of
 * them. The other party's policies and fee schedule templates always stay behind, and a client's
 * rates always lose the firm's people they were staffed with, because those name the firm's staffing
 * rather than a team the client chose. Without their own team, the saver's rates lose their people
 * too, since a rate may name only somebody on the template's team.
 */
export function saveProposalTemplatePayload(args: {
  name: string;
  party: ProposalTemplateParty;
  /** The proposal the template is saved from, for the name a draft will start with. */
  source: Proposal;
  /** The proposal as a new draft would carry it over. */
  carried: Proposal;
  /** Which teams to keep. Without it, the saver's own team alone, as before #6637. */
  teams?: ProposalTemplateTeamChoices;
}): SaveProposalTemplatePayload {
  const { name, party, source, carried } = args;
  const teams = args.teams ?? DEFAULT_TEAM_CHOICES;
  const asVendor = party === ProposalTemplateParty.Vendor;

  const team = asVendor ? carried.vendorTeam : carried.clientTeam;
  const otherTeam = asVendor ? carried.clientTeam : carried.vendorTeam;
  const keepsStaffing = asVendor && teams.ownTeam;
  const feeSchedule = Masked.isNotMasked(carried.feeSchedule) ? carried.feeSchedule : [];
  const policies = asVendor ? carried.vendorPolicies : carried.clientPolicies;

  return {
    name,
    party,
    proposalName: source.name?.value ?? "",
    description: carried.description?.value ?? "",
    negotiable: carried.negotiable.value,
    teamRestricted: Masked.isPresentNotMasked(carried.teamRestricted) ? carried.teamRestricted.value : false,
    conflictsCheckWaived: Masked.isPresentNotMasked(carried.conflictsCheckWaived)
      ? carried.conflictsCheckWaived.value
      : false,
    team: teams.ownTeam ? teamPayload(team) : undefined,
    feeSchedule: feeSchedule.map(
      (category) =>
        new FeeScheduleCategoryCommand(keepsStaffing ? category : withoutFeeScheduleTeamMember(category)).payload,
    ),
    discount: Masked.isPresentNotMasked(carried.discount) ? carried.discount.value : 0,
    teamTemplateIds: teams.ownTeam
      ? (asVendor ? carried.vendorTeamTemplateIds : carried.clientTeamTemplateIds).map((id) => id.value)
      : [],
    sourceProposalId: source.id?.value,
    counterpartyTeam: teams.otherTeam ? teamPayload(otherTeam) : undefined,
    // From the source, not `carried`: a draft carries over only its own side's template lists, so
    // the other party's are always empty there (review of #6798).
    counterpartyTeamTemplateIds:
      teams.otherTeam && teams.otherTeamTemplates
        ? (asVendor ? source.clientTeamTemplateIds : source.vendorTeamTemplateIds).map((id) => id.value)
        : [],
    feeScheduleTemplateIds: (asVendor
      ? carried.vendorFeeScheduleTemplateIds
      : carried.clientFeeScheduleTemplateIds
    ).map((id) => id.value),
    conflicts: documentIdsOf(carried.conflicts),
    policies: documentIdsOf(policies),
  };
}

/** A team as the template keeps it, or nothing for one the saver cannot see or that has nobody on it. */
function teamPayload(team: Proposal["clientTeam"] | Proposal["vendorTeam"]): TeamPayload | undefined {
  if (!team || !Masked.isNotMasked(team) || (!team.leader && team.members.length === 0)) return undefined;
  return {
    leader: team.leader ? person(team.leader) : undefined,
    members: team.members.filter((member) => !member.isSamePersonAs(team.leader)).map(person),
    marketplaceId: "marketplaceId" in team ? team.marketplaceId?.value : undefined,
  };
}

function person(member: EntityMember): PersonPayload {
  return { userId: member.userId.value, entityId: member.legalEntityId.value };
}

function documentIdsOf(documents: WorkDocument[] | MaskedContracts): string[] {
  return Masked.isNotMasked(documents) ? documents.map((document) => document.id.value) : [];
}
